import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import belowIcon from '../../asset/icon/below.png';
import deleteIcon from '../../asset/icon/delete.png';
import upIcon from '../../asset/icon/up.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { Language } from '../../shared/enum/EnumRepository';
import { useExhibitionVenue } from '../../shared/hooks/useApi/useExhibitionVenue';
import { useLanguageStore } from '../../shared/store/languageStore';
import { openNewTab } from '../../shared/util/openNewTab';
import {
  getInterestExhibitionEndDateDisplay,
  getInterestExhibitionTargetUrl,
  groupInterestExhibitionsByVenue,
  type InterestExhibitionCardData,
} from '../../shared/util/interestExhibitions';
import { getVenueDisplayName } from '../../shared/util/venueTypeLabel';

const VISIBLE_ARTIST_COUNT = 4;

type NamedArtist = {
  artist: InterestExhibitionCardData['exhibition']['artists'][number];
  name: string;
};

const ArtistNames: React.FC<{ artists: NamedArtist[] }> = ({ artists }) => (
  <>
    {artists.map(({ artist, name }, index) => {
      const instagramUrl = getArtistInstagramUrl(artist.instagramHandle);
      return (
        <React.Fragment key={`${artist.artistId ?? artist.name}-${index}`}>
          {index > 0 && ', '}
          {instagramUrl ? (
            <ArtistButton
              type="button"
              onClick={() => openNewTab(instagramUrl)}
            >
              {name}
            </ArtistButton>
          ) : (
            <ArtistName>{name}</ArtistName>
          )}
        </React.Fragment>
      );
    })}
  </>
);

const getExhibitionTitle = (
  card: InterestExhibitionCardData,
  language: Language,
): string => {
  const { title, titleEn } = card.exhibition;
  return language === Language.EN ? titleEn.trim() || title : title;
};

const getArtistName = (
  artist: InterestExhibitionCardData['exhibition']['artists'][number],
  language: Language,
): string => (
  language === Language.EN ? (artist.nameEn ?? '').trim() || artist.name : artist.name
);

const getArtistInstagramUrl = (handle: string): string => {
  const normalizedHandle = handle.trim().replace(/^@/, '');
  return normalizedHandle
    ? `https://www.instagram.com/${normalizedHandle}/`
    : '';
};

const InterestExhibitionContainer: React.FC = () => {
  const containerRef = useRef<HTMLElement>(null);
  const language = useLanguageStore((state) => state.language);
  const {
    venueList,
    interestExhibitions,
    loadingVenueIds,
    removeInterestVenue,
  } = useExhibitionVenue();
  const venueGroups = useMemo(
    () => groupInterestExhibitionsByVenue(interestExhibitions),
    [interestExhibitions],
  );
  const previousVenueCountRef = useRef(venueGroups.length);
  const [expandedVenueIds, setExpandedVenueIds] = useState<Set<string>>(() => new Set());
  const [expandedArtistKeys, setExpandedArtistKeys] = useState<Set<string>>(() => new Set());

  useLayoutEffect(() => {
    const previousVenueCount = previousVenueCountRef.current;
    previousVenueCountRef.current = venueGroups.length;
    if (venueGroups.length <= previousVenueCount) return;

    const container = containerRef.current;
    container?.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [venueGroups.length]);

  const toggleVenueExhibitions = (venueId: string) => {
    setExpandedVenueIds((current) => {
      const next = new Set(current);
      if (next.has(venueId)) next.delete(venueId);
      else next.add(venueId);
      return next;
    });
  };

  const toggleArtists = (exhibitionKey: string) => {
    setExpandedArtistKeys((current) => {
      const next = new Set(current);
      if (next.has(exhibitionKey)) next.delete(exhibitionKey);
      else next.add(exhibitionKey);
      return next;
    });
  };

  const containerLabel = language === Language.KR
    ? '관심 전시'
    : 'Interested exhibitions';
  const collapseLabel = language === Language.KR ? '접기' : 'Show less';

  return (
    <Container
      ref={containerRef}
      aria-label={containerLabel}
      onClick={(event) => event.stopPropagation()}
    >
      {venueGroups.map((group) => {
        const venueName = getVenueDisplayName({
          name: group.venueName,
          nameEn: group.venueNameEn,
        }, language);
        const isVenueExpanded = expandedVenueIds.has(group.venueId);
        const representativeExhibition = group.exhibitions[0];
        const extraExhibitions = group.exhibitions.slice(1);
        const hiddenExhibitionCount = extraExhibitions.length;
        if (!representativeExhibition) return null;
        const removeLabel = language === Language.KR
          ? `${venueName} 관심 전시에서 제거`
          : `Remove ${venueName} from interested exhibitions`;
        const toggleExhibitionsLabel = language === Language.KR
          ? (isVenueExpanded ? `${venueName} 전시 접기` : `${venueName} 전시 더 보기`)
          : (isVenueExpanded
            ? `Collapse exhibitions at ${venueName}`
            : `Show more exhibitions at ${venueName}`);

        return (
          <VenueCard key={group.venueId}>
            <RemoveButtonContainer>
              <UniversalIconBtn
                size="small"
                border={false}
                isTransparent
                hoverOpacity
                backgroundImg={deleteIcon}
                aria-label={removeLabel}
                onClick={() => removeInterestVenue(group.venueId)}
              />
            </RemoveButtonContainer>
            {group.venueNaverMapUrl ? (
              <VenueLink
                type="button"
                onClick={() => openNewTab(group.venueNaverMapUrl)}
              >
                {venueName}
              </VenueLink>
            ) : (
              <VenueName>{venueName}</VenueName>
            )}
            <ExhibitionBlock key={representativeExhibition.key}>
              <ExhibitionBody
                card={representativeExhibition}
                language={language}
                artistsExpanded={expandedArtistKeys.has(representativeExhibition.key)}
                collapseLabel={collapseLabel}
                onToggleArtists={() => toggleArtists(representativeExhibition.key)}
              />
            </ExhibitionBlock>
            {hiddenExhibitionCount > 0 && (
              <ExpandableCell $isExpanded={isVenueExpanded}>
                <ExpandableCellInner>
                  {extraExhibitions.map((card) => (
                    <ExtraExhibitionBlock key={card.key}>
                      <ExhibitionBody
                        card={card}
                        language={language}
                        artistsExpanded={expandedArtistKeys.has(card.key)}
                        collapseLabel={collapseLabel}
                        onToggleArtists={() => toggleArtists(card.key)}
                      />
                    </ExtraExhibitionBlock>
                  ))}
                </ExpandableCellInner>
              </ExpandableCell>
            )}
            {hiddenExhibitionCount > 0 && (
              <ToggleButtonContainer>
                <UniversalIconBtn
                  size="small"
                  border={false}
                  isTransparent
                  hoverOpacity
                  backgroundImg={isVenueExpanded ? upIcon : belowIcon}
                  aria-label={toggleExhibitionsLabel}
                  aria-expanded={isVenueExpanded}
                  onClick={() => toggleVenueExhibitions(group.venueId)}
                />
              </ToggleButtonContainer>
            )}
          </VenueCard>
        );
      })}
      {loadingVenueIds.map((venueId) => {
        const venue = venueList.find((item) => String(item.id) === venueId);
        const venueName = venue
          ? getVenueDisplayName(venue, language)
          : venueId;
        return (
          <LoadingCard key={`loading-${venueId}`}>
            <VenueName>{venueName}</VenueName>
            <LoadingText>
              {language === Language.KR ? '전시를 불러오는 중…' : 'Loading exhibitions…'}
            </LoadingText>
          </LoadingCard>
        );
      })}
    </Container>
  );
};

interface ExhibitionBodyProps {
  card: InterestExhibitionCardData;
  language: Language;
  artistsExpanded: boolean;
  collapseLabel: string;
  onToggleArtists: () => void;
}

const ExhibitionBody: React.FC<ExhibitionBodyProps> = ({
  card,
  language,
  artistsExpanded,
  collapseLabel,
  onToggleArtists,
}) => {
  const title = getExhibitionTitle(card, language);
  const artists = card.exhibition.artists
    .map((artist) => ({ artist, name: getArtistName(artist, language) }))
    .filter(({ name }) => Boolean(name));
  const previewArtists = artists.slice(0, VISIBLE_ARTIST_COUNT);
  const extraArtists = artists.slice(VISIBLE_ARTIST_COUNT);
  const hiddenArtistCount = extraArtists.length;
  const endDate = getInterestExhibitionEndDateDisplay(card.exhibition.endDate);
  const exhibitionUrl = getInterestExhibitionTargetUrl(
    card.exhibition.officialUrl,
    card.venueInstagramUrl,
  );
  const moreArtistsLabel = artistsExpanded
    ? collapseLabel
    : language === Language.KR
      ? `외 ${hiddenArtistCount}명`
      : `${hiddenArtistCount} more`;

  return (
    <>
      {exhibitionUrl ? (
        <TitleButton
          type="button"
          onClick={() => openNewTab(exhibitionUrl)}
        >
          {title}
        </TitleButton>
      ) : (
        <Title>{title}</Title>
      )}
      <EndDate>
        {endDate.text}
        {endDate.dDayText && <DdayText> {endDate.dDayText}</DdayText>}
      </EndDate>
      {artists.length > 0 && (
        <Artists>
          <ArtistNames artists={previewArtists} />
          {hiddenArtistCount > 0 && (
            <>
              {' '}
              <MoreButton type="button" onClick={onToggleArtists}>
                {moreArtistsLabel}
              </MoreButton>
              <ExpandableCell $isExpanded={artistsExpanded}>
                <ExpandableCellInner>
                  <ExtraArtists>
                    <ArtistNames artists={extraArtists} />
                  </ExtraArtists>
                </ExpandableCellInner>
              </ExpandableCell>
            </>
          )}
        </Artists>
      )}
    </>
  );
};

const Container = styled.section`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const VenueCard = styled.article`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 28px 16px 0;
  box-sizing: border-box;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 0.5px;
    background: ${({ theme }) => theme.colors.color_Gray_06};
  }
`;

const ExhibitionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ExtraExhibitionBlock = styled(ExhibitionBlock)`
  padding-top: 10px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};

  & + & {
    margin-top: 10px;
  }
`;

const ExpandableCell = styled.div<{ $isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? '1fr' : '0fr')};
  min-width: 0;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  visibility: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'none')};
  transition:
    grid-template-rows 0.45s ease-in-out,
    opacity 0.3s ease-in-out,
    visibility 0s ${({ $isExpanded }) => ($isExpanded ? '0s' : '0.45s')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ExpandableCellInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const LoadingCard = styled(VenueCard)`
  min-height: 72px;
`;

const RemoveButtonContainer = styled.div`
  position: absolute;
  top: -4px;
  right: 0;
  display: flex;
`;

const ToggleButtonContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 8px;
  display: flex;
`;

const VenueName = styled.div`
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Footer_01};
`;

const VenueLink = styled.button`
  width: fit-content;
  max-width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transform-origin: left center;
  transition: transform 0.2s ease-in-out;
  ${({ theme }) => theme.typography.Footer_01};

  &:hover,
  &:focus-visible {
    transform: scale(0.9);
  }
`;

const titleStyle = `
  width: fit-content;
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const Title = styled.div`
  ${titleStyle}
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const TitleButton = styled.button`
  ${titleStyle}
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_03_2};

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const EndDate = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const DdayText = styled.span`
  color: ${({ theme }) => theme.colors.color_alert_red};
`;

const Artists = styled.div`
  padding-top: 2px;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Footer_01};
`;

const ExtraArtists = styled.div`
  padding-top: 2px;
  overflow-wrap: anywhere;
`;

const ArtistButton = styled.button`
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  ${({ theme }) => theme.typography.Footer_01};

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const ArtistName = styled.span`
  color: inherit;
`;

const MoreButton = styled.button`
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: left;
  cursor: pointer;
  ${({ theme }) => theme.typography.Footer_01};

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const LoadingText = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

export default InterestExhibitionContainer;
